import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { contentResource } from './api';
import { getCollection } from '@/config/collections';
import { keys } from '@/lib/query/keys';
import { Dialog } from '@/lib/ui/Dialog';
import { DataView } from '@/lib/ui/states';
import { TextInput } from '@/lib/ui/Field';
import { useLocalListState } from '@/lib/hooks/useListState';
import { useSearchInput } from '@/lib/hooks/useSearchInput';
import { ListEmpty } from '@/lib/ui/ListEmpty';
import { useLocale } from '@/lib/i18n/useLocale';
import { resolveI18n } from '@/lib/i18n/content';
import { MonoValue } from '@/lib/ui/Typography';

/** Pick a content reference for a format:uuid + x-entity field (spec §9.1). */
export function EntityPickerModal({
  collection,
  open,
  onClose,
}: {
  collection: string;
  open: boolean;
  onClose: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  // A list inside a modal does not own the address it opens over: local list state.
  const list = useLocalListState();
  const [q, setQ] = useSearchInput(list, 250);
  const res = contentResource(collection);
  const cfg = getCollection(collection);
  const collectionLabel = cfg ? t(cfg.label, { defaultValue: cfg.label }) : collection;

  const query = useQuery({
    queryKey: keys.content.list(collection, { ...list.params, per_page: 25 }),
    queryFn: () => res.list({ ...list.params, per_page: 25 }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(null)} title={t('content.choose', { label: collectionLabel })} size="md">
      <TextInput label={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      <div className="mt-md">
        <DataView
          query={query}
          isEmpty={(d) => d.data.length === 0}
          copy={{
            loading: t('states.entityPicker.loading', { label: collectionLabel }),
            errorTitle: t('states.entityPicker.errorTitle', { label: collectionLabel }),
          }}
          empty={
            <ListEmpty
              filtered={list.active}
              onClearFilters={list.reset}
              title={t('states.entityPicker.emptyTitle')}
              body={t('states.entityPicker.emptyBody', { label: collectionLabel })}
            />
          }
        >
          {(d) => (
            <ul className="divide-y divide-hairline rounded-md border-[0.5px] border-hairline">
              {d.data.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onClose(item.id)}
                    className="flex min-h-[44px] w-full items-center justify-between gap-sm px-md py-sm text-start transition-colors duration-almanac hover:bg-canvas-sand focus-visible:shadow-focus"
                  >
                    <span className="text-small text-ink-graphite">{resolveI18n(item.title, locale)}</span>
                    <MonoValue className="text-label-sm text-ink-mute">
                      {item.slug || item.key}
                    </MonoValue>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DataView>
      </div>
    </Dialog>
  );
}
